/**
 * VB-024B.4 — Centralized capability system
 *
 * Single source of truth for user tiers, capabilities, and feature gating.
 * No plan checks dispersed in the app: every feature verifies a capability,
 * every tier activates a set of capabilities.
 *
 * Architecture targets:
 *   VISITOR   — anonymous exploration (localStorage only, limited entity types)
 *   FREE      — authenticated account (Supabase persistence, full entity access)
 *   PRO       — future: media, AI, multi-brain (not yet wired to billing)
 *   BUSINESS  — future: team workspaces (not yet wired to billing)
 */

import type { Session } from '@supabase/supabase-js';
import type { EntityType } from '../types';

// ─── Tiers ───────────────────────────────────────────────────────────────────

export type UserTier = 'VISITOR' | 'FREE' | 'PRO' | 'MAX' | 'BUSINESS';

// ─── Capabilities ─────────────────────────────────────────────────────────────

export type Capability =
  /** Persist data to Supabase. VISITOR: localStorage only. */
  | 'can_save_remotely'
  /** Create events, habits, contacts, journal entries. VISITOR: task/note/goal only. */
  | 'can_use_all_entities'
  /** Access inbox, calendar, events, contacts, habits, journal, domains, months, finance. */
  | 'can_view_all_pages'
  /** Upload media / file attachments. (future — PRO+) */
  | 'can_add_media'
  /** AI-powered features. (future — PRO+) */
  | 'can_use_ai'
  /** Multiple independent brains. (future — PRO+) */
  | 'can_multi_brain'
  /** Team / collaborative workspace. (future — BUSINESS) */
  | 'can_team_workspace';

// ─── Tier → capabilities mapping (single source of truth) ────────────────────

const TIER_CAPABILITIES: Record<UserTier, ReadonlySet<Capability>> = {
  VISITOR: new Set<Capability>([
    // No remote persistence, no extended entities, no locked pages.
  ]),

  FREE: new Set<Capability>([
    'can_save_remotely',
    'can_use_all_entities',
    'can_view_all_pages',
    'can_use_ai',
  ]),

  PRO: new Set<Capability>([
    'can_save_remotely',
    'can_use_all_entities',
    'can_view_all_pages',
    'can_add_media',
    'can_use_ai',
    'can_multi_brain',
  ]),

  MAX: new Set<Capability>([
    'can_save_remotely',
    'can_use_all_entities',
    'can_view_all_pages',
    'can_add_media',
    'can_use_ai',
    'can_multi_brain',
  ]),

  BUSINESS: new Set<Capability>([
    'can_save_remotely',
    'can_use_all_entities',
    'can_view_all_pages',
    'can_add_media',
    'can_use_ai',
    'can_multi_brain',
    'can_team_workspace',
  ]),
};

// ─── Core helpers ─────────────────────────────────────────────────────────────

/**
 * Derive the user tier from current auth state.
 * All authenticated users are FREE until billing tiers are implemented.
 */
export function getUserTier(session: Session | null): UserTier {
  if (session) return 'FREE';
  return 'VISITOR';
}

/**
 * Return the effective tier taking into account an active trial period.
 * If trialEndsAt is set and in the future, the user gets full PRO access.
 * Otherwise the original plan is returned as-is.
 */
export function getEffectiveTier(plan: string, trialEndsAt: string | null): string {
  if (plan !== 'free') return plan;
  if (trialEndsAt && new Date(trialEndsAt) > new Date()) {
    return 'pro';
  }
  return 'free';
}

/** Return the full set of capabilities for a given tier. */
export function getCapabilitiesForTier(tier: UserTier): ReadonlySet<Capability> {
  return TIER_CAPABILITIES[tier];
}

/** Return true when the given tier has the given capability. */
export function hasCapability(tier: UserTier, capability: Capability): boolean {
  return TIER_CAPABILITIES[tier].has(capability);
}

// ─── Numeric limits per tier ──────────────────────────────────────────────────

export interface TierLimits {
  maxNodes: number; // -1 = illimité
  maxBrains: number; // -1 = illimité
  aiCreditsPerMonth: number; // -1 = illimité
  maxObjectives: number; // -1 = illimité
  maxContacts: number; // -1 = illimité
  maxHabits: number; // -1 = illimité
  price: number; // €/mois, 0 = gratuit
  priceLabel: string; // affiché tel quel
}

export const TIER_LIMITS: Record<Exclude<UserTier, 'VISITOR'>, TierLimits> = {
  FREE: {
    maxNodes: 50,
    maxBrains: 1,
    aiCreditsPerMonth: 50,
    maxObjectives: 3,
    maxContacts: 10,
    maxHabits: 3,
    price: 0,
    priceLabel: 'Gratuit',
  },
  PRO: {
    maxNodes: 500,
    maxBrains: 3,
    aiCreditsPerMonth: 300,
    maxObjectives: -1,
    maxContacts: -1,
    maxHabits: -1,
    price: 9,
    priceLabel: '9€/mois',
  },
  MAX: {
    maxNodes: -1,
    maxBrains: -1,
    aiCreditsPerMonth: 2000,
    maxObjectives: -1,
    maxContacts: -1,
    maxHabits: -1,
    price: 20,
    priceLabel: '20€/mois',
  },
  BUSINESS: {
    maxNodes: -1,
    maxBrains: -1,
    aiCreditsPerMonth: 2000,
    maxObjectives: -1,
    maxContacts: -1,
    maxHabits: -1,
    price: 20,
    priceLabel: '~20€/utilisateur/mois',
  },
};

/**
 * Return the TierLimits for the given tier string (case-insensitive).
 * Falls back to FREE limits when the tier is unknown or VISITOR.
 */
export function getTierLimits(tier: string): TierLimits {
  const upper = tier.toUpperCase() as Exclude<UserTier, 'VISITOR'>;
  return TIER_LIMITS[upper] ?? TIER_LIMITS.FREE;
}

// ─── Entity type gating ───────────────────────────────────────────────────────

/**
 * Entity types available in VISITOR mode — single source of truth.
 * All other entity types require `can_use_all_entities`.
 */
export const VISITOR_ALLOWED_ENTITY_TYPES: ReadonlyArray<EntityType> = ['task', 'note', 'goal'];

/**
 * Return true when the given entity type can be created at the given tier.
 */
export function canCreateEntityType(tier: UserTier, entityType: EntityType | string): boolean {
  if (hasCapability(tier, 'can_use_all_entities')) return true;
  return (VISITOR_ALLOWED_ENTITY_TYPES as string[]).includes(entityType);
}

// ─── Page access gating ───────────────────────────────────────────────────────

/**
 * Return true when the given page is accessible at the given tier.
 */
export function isPageAllowed(tier: UserTier, _page: string): boolean {
  if (hasCapability(tier, 'can_view_all_pages')) return true;
  // Future: add per-page gating for PRO/BUSINESS tiers here
  return true;
}

// ─── View access gating (premium views, per-tier) ─────────────────────────────

export const VIEW_ACCESS: Record<
  string,
  {
    requiredTier: 'pro' | 'max';
    viewName: string;
    description: string;
    comingSoon: boolean;
  }
> = {
  finances: {
    requiredTier: 'pro',
    viewName: 'Finances',
    description: "Suivez vos revenus, dépenses et objectifs financiers en un coup d'œil.",
    comingSoon: false,
  },
  kanban: {
    requiredTier: 'pro',
    viewName: 'Kanban',
    description: 'Organisez vos tâches en colonnes visuelles pour un workflow agile et fluide.',
    comingSoon: false,
  },
  gantt: {
    requiredTier: 'max',
    viewName: 'Gantt',
    description:
      'Planifiez vos projets sur une timeline interactive avec dépendances entre tâches.',
    comingSoon: false,
  },
};

/**
 * Return true when the given view key is accessible at the given tier.
 * Tier comparison is case-insensitive ('FREE', 'free', etc. all accepted).
 */
export function canAccessView(viewKey: string, tier: string): boolean {
  const access = VIEW_ACCESS[viewKey];
  if (!access) return true;
  const t = tier.toLowerCase();
  if (access.requiredTier === 'pro') return t !== 'free' && t !== 'visitor';
  if (access.requiredTier === 'max') return t === 'max' || t === 'business';
  return true;
}
